import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function ExamTake() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { refreshMe } = useAuth();
    const [exam, setExam] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get(`/exams/${id}`).then((r) => setExam(r.data));
    }, [id]);

    async function submit() {
        setSubmitting(true);
        try {
            const arr = exam.questions.map((_, i) => answers[i] ?? -1);
            const { data } = await api.post(`/exams/${id}/submit`, { answers: arr });
            await refreshMe();
            setResult(data);
            toast.success(`¡Examen enviado! ${data.score_percent}%`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error enviando examen");
        } finally {
            setSubmitting(false);
        }
    }

    if (!exam) return <div className="p-6 text-edu-muted">Cargando…</div>;

    if (result) {
        return (
            <div className="p-6 md:p-10 max-w-2xl mx-auto fade-in">
                <div className="edu-card p-8 text-center" data-testid="exam-result">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                        <CheckCircle2 size={32} className="text-emerald-600" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">¡Examen completado!</h1>
                    <p className="text-edu-body mb-5">{exam.title}</p>
                    <div className="text-6xl font-bold text-edu-navy mb-2">{result.score_percent}%</div>
                    <div className="text-edu-muted mb-6">
                        {result.correct} de {result.total} correctas · {result.grade}
                    </div>
                    <button
                        onClick={() => navigate("/alumno/examenes")}
                        className="edu-btn-primary px-6 py-3 text-sm"
                        data-testid="exam-result-back"
                    >
                        Volver a Exámenes
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-10 max-w-3xl mx-auto fade-in" data-testid="exam-take-page">
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-sm text-edu-navy mb-3 hover:underline"
                data-testid="exam-back"
            >
                <ArrowLeft size={16} /> Volver
            </button>
            <h1 className="text-3xl font-semibold mb-1">{exam.title}</h1>
            <p className="text-edu-body mb-6">
                {exam.subject} · {exam.duration_minutes} min · {exam.questions.length} preguntas
            </p>

            <div className="space-y-5">
                {exam.questions.map((q, qi) => (
                    <div key={qi} className="edu-card p-6" data-testid={`question-${qi}`}>
                        <h3 className="font-semibold mb-3">
                            {qi + 1}. {q.question}
                        </h3>
                        <div className="space-y-2">
                            {q.options.map((opt, oi) => (
                                <label
                                    key={oi}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                                        answers[qi] === oi
                                            ? "border-edu-navy bg-edu-navy/5"
                                            : "border-edu hover:bg-gray-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name={`q-${qi}`}
                                        checked={answers[qi] === oi}
                                        onChange={() => setAnswers({ ...answers, [qi]: oi })}
                                        data-testid={`q-${qi}-opt-${oi}`}
                                    />
                                    <span className="text-sm">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <button
                onClick={submit}
                disabled={submitting || Object.keys(answers).length !== exam.questions.length}
                className="edu-btn-primary w-full mt-6 py-3.5 disabled:opacity-50"
                data-testid="exam-submit"
            >
                {submitting ? "Enviando…" : "Enviar Examen"}
            </button>
        </div>
    );
}
